import React from 'react';
import { Skeleton, styled, Typography, TypographyProps } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import { isDefined } from 'lib/typeguards';
import { useCurrencyString } from 'lib/hooks/useCurrencyString';
import { Row } from 'ui/Flex';
import { formatAnalyticsMeasureValue } from 'components/analytics/audiences/model';
import { TotalsBoxPopup } from './TotalsBoxPopup';
import { MetricCard } from './MetricCard';

interface TotalsBoxMeasureRecord {
    value?: number;
    loading?: boolean;
}

export interface TotalsBoxMeasure {
    key: string;
    current: TotalsBoxMeasureRecord;
    retro: TotalsBoxMeasureRecord;
    difference?: number;
}

interface TotalsBoxProps {
    active?: boolean;
    measure: TotalsBoxMeasure;
    onClick?: (key: string) => void;
    datePoints: [Date, Date, Date];
    title: string;
    subtitle?: string;
    format: 'number' | 'currency';
    description?: string;
    differenceFormat?: 'percent' | 'decimal';
    mainMetricFontSize?: TypographyProps['variant'];
    footer?: React.ReactNode;
}

const InteractiveTypography = styled(Typography)(() => ({
    textDecoration: 'underline',
    textDecorationStyle: 'dashed',
    cursor: 'help',
    '&:hover': {
        textDecorationStyle: 'solid'
    }
}));

const InteractiveCaption = styled(Typography)(({ theme }) => ({
    cursor: 'help',
    position: 'relative',
    '&:before': {
        position: 'absolute',
        content: 'no-open-quote',
        display: 'block',
        bottom: theme.spacing(0.25),
        left: theme.spacing(0.5),
        right: theme.spacing(0.5),
        borderColor: 'inherit',
        borderBottom: '1px dashed'
    },
    '&:hover': {
        '&:before': {
            borderBottom: '1px solid'
        }
    }
}));

export const TotalsBox: React.FC<TotalsBoxProps> = ({
    active,
    measure,
    onClick,
    datePoints,
    title,
    subtitle,
    format,
    description,
    differenceFormat = 'percent',
    footer,
    mainMetricFontSize = 'h5'
}) => {
    const getCurrencyString = useCurrencyString();
    const value = React.useMemo(
        () => formatAnalyticsMeasureValue(measure.current?.value, format, getCurrencyString),
        [format, getCurrencyString, measure]
    );
    const retroValue = React.useMemo(
        () => formatAnalyticsMeasureValue(measure.retro?.value, format, getCurrencyString),
        [format, getCurrencyString, measure]
    );
    const difference = React.useMemo(
        () => (measure.current?.value ?? 0) - (measure.retro?.value ?? 0),
        [measure]
    );

    const currentValuesLoading = React.useMemo(() => measure.current?.loading ?? true, [measure]);
    const retroValuesLoading = React.useMemo(() => measure.retro?.loading ?? true, [measure.retro?.loading]);

    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(measure.key);
        }
    }, [measure.key, onClick]);

    const [anchor, setAnchor] = React.useState<HTMLDivElement | null>(null);
    const handleMouseEnter = React.useCallback((e: React.MouseEvent) => {
        setAnchor(e.currentTarget as HTMLDivElement);
    }, []);
    const handleMouseLeave = React.useCallback(() => {
        setAnchor(null);
    }, []);

    return (
        <MetricCard active={active} clickable={!!onClick} onClick={handleClick}>
            <InteractiveTypography
                variant="body2"
                sx={{
                    fontWeight: 'bold',
                    opacity: 0.75
                }}
                aria-owns={!!anchor ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {title}
            </InteractiveTypography>
            <Row valign="flex-end">
                <Typography
                    variant={mainMetricFontSize}
                    sx={{
                        fontWeight: 'bold',
                        mt: 0.25
                    }}
                >
                    {currentValuesLoading ? <Skeleton width={100} /> : value}
                </Typography>
                {retroValuesLoading && <Skeleton sx={{ ml: 0.5 }} variant="text" width={60} height={26} />}
                {!retroValuesLoading && isDefined(measure.difference) && (
                    <InteractiveCaption
                        variant="caption"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        sx={{
                            color: measure.difference >= 0 ? 'success.main' : 'error.main',
                            borderColor: measure.difference >= 0 ? 'success.main' : 'error.main',
                            ml: 0.5,
                            pb: 0.25,
                            fontWeight: 600,
                            fontSize: '0.8rem'
                        }}
                    >
                        (
                        {differenceFormat === 'percent'
                            ? new Intl.NumberFormat('en-GB', {
                                  style: 'percent',
                                  maximumFractionDigits: 1
                              }).format(measure.difference)
                            : `${measure.difference >= 0 ? '+' : ''}${new Intl.NumberFormat('en-GB', {
                                  style: 'decimal',
                                  maximumFractionDigits: 1
                              }).format(measure.difference)}`}
                        <ArrowOutwardIcon
                            color="inherit"
                            sx={{
                                fontSize: '0.8rem',
                                transform: measure.difference < 0 ? 'rotate(90deg)' : undefined,
                                mt: 0.25
                            }}
                        />
                        )
                    </InteractiveCaption>
                )}
            </Row>
            {subtitle && (
                <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.6, mt: 0.25 }}>
                    {subtitle}
                </Typography>
            )}
            {footer}
            <TotalsBoxPopup
                title={title}
                subtitle={description}
                anchor={anchor}
                onClose={handleMouseLeave}
                datePoints={datePoints}
                value={value}
                retroValue={retroValue}
                valueLoading={currentValuesLoading}
                retroValueLoading={retroValuesLoading}
                difference={difference}
            />
        </MetricCard>
    );
};
